import { useOptionalPostHog } from '@/lib/Hooks/PostHogProvider';
import { useWebView } from '@/lib/Hooks/useIsWebViewContext';
import {
  PRODUCT_ANALYTICS_EVENTS,
  captureProductEvent,
  normalizeAnalyticsPath,
} from '@/lib/Analytics/productEvents';
import { sanitizeRedirectUrl } from '@/lib/Auth/redirectSafety';
import { useBrand } from '@/lib/brand/BrandContext';
import type { EnabledAuthProvider } from '@/lib/brand/types';
import { Routes } from '@/lib/constants';
import { useCreateUser } from '@/lib/react-query/auth/useCreateUser';
import { useResendVerification } from '@/lib/react-query/auth/useResendVerification';
import { zodResolver } from '@hookform/resolvers/zod';
import { userLoginSchema, userRegisterSchema } from 'lib-server/validation';
import {
  getErrorMessage,
  mapSignInErrorToUserMessage,
  sanitizeAuthErrorMessage,
} from './authErrorMessages';
import ResendVerificationCallout, {
  EMAIL_NOT_VERIFIED_ERROR,
  RESEND_FAILURE_MESSAGE,
  RESEND_SUCCESS_MESSAGE,
  type ResendStatus,
} from './ResendVerificationCallout';
import AuthCredentialForm from './AuthCredentialForm';
import AuthSuccessRegister from './AuthSuccessRegister';

import { getProviders, signIn, SignInResponse } from 'next-auth/react';
import { useRouter } from 'next/router';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { UserCreateFormData } from 'types/models/User';
import Alert from '../Alert';

type Props = {
  isRegisterForm?: boolean;
  providers: Awaited<ReturnType<typeof getProviders>>;
};

export { sanitizeAuthErrorMessage };

const getAuthBaseUrl = (brandOrigin: string) =>
  typeof window === 'undefined' ? brandOrigin : window.location.origin;

const getCallbackUrlParam = (value: unknown) =>
  typeof value === 'string' ? value : '';

const AuthForm: FC<Props> = ({ isRegisterForm = true, providers }) => {
  const brand = useBrand();
  const { isWebView } = useWebView();
  const methods = useForm<UserCreateFormData>({
    resolver: zodResolver(
      isRegisterForm ? userRegisterSchema : userLoginSchema
    ),
  });
  const router = useRouter();
  const posthog = useOptionalPostHog();
  const signupTelemetryStateRef = useRef<'success' | 'error' | null>(null);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [isComponentLoading, setIsComponentLoading] = useState(true);
  const [signInError, setSignInError] = useState('');
  const [signInErrorCode, setSignInErrorCode] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [cleverLoading, setCleverLoading] = useState(false);
  const [lastAttemptedEmail, setLastAttemptedEmail] = useState('');
  const [resendStatus, setResendStatus] = useState<ResendStatus>({
    kind: 'idle',
  });
  const [cooldownSecondsRemaining, setCooldownSecondsRemaining] = useState<
    number | null
  >(null);
  const {
    mutate: createUser,
    status,
    isError,
    error,
    isSuccess,
    data,
    isPending: isLoading,
  } = useCreateUser();
  const {
    mutateAsync: resendVerification,
    isPending: isResendingVerification,
  } = useResendVerification();
  const currentEmailValue = methods.watch('email');

  useEffect(() => {
    const handleRouteChangeStart = () => setLoadingDashboard(true);
    const handleRouteChangeEnd = () => setLoadingDashboard(false);

    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeComplete', handleRouteChangeEnd);
    router.events.on('routeChangeError', handleRouteChangeEnd);

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeComplete', handleRouteChangeEnd);
      router.events.off('routeChangeError', handleRouteChangeEnd);
    };
  }, [router.events]);

  const routedErrorParam = Array.isArray(router.query?.error)
    ? router.query?.error[0]
    : router.query?.error;

  const authBaseUrl = getAuthBaseUrl(brand.publicOrigin);
  const defaultCallbackPath = Routes.SITE.DASHBOARD;
  const callbackUrl = sanitizeRedirectUrl({
    url: getCallbackUrlParam(router.query?.callbackUrl),
    baseUrl: authBaseUrl,
    fallbackUrl: new URL(defaultCallbackPath, authBaseUrl).toString(),
    allowedOrigins: brand.allowedRedirectOrigins,
    allowRelativePathWithoutSlash: true,
  });

  const cleverProvider = providers
    ? Object.values(providers).find((provider) => provider.id === 'clever')
    : undefined;

  const showCleverSignIn = Boolean(
    cleverProvider &&
    brand.enabledAuthProviders.includes(
      cleverProvider.id as EnabledAuthProvider
    )
  );

  const SIGNIN_TIMEOUT_MS = 20000;

  const resetResendState = useCallback(() => {
    setResendStatus({ kind: 'idle' });
    setCooldownSecondsRemaining(null);
  }, []);

  const handleCleverSignIn = () => {
    if (!cleverProvider) return;
    setSignInError('');
    setCleverLoading(true);
    captureProductEvent(
      posthog,
      PRODUCT_ANALYTICS_EVENTS.AUTH_CLEVER_SIGN_IN_CLICKED,
      {
        form_type: isRegisterForm ? 'register' : 'signin',
      }
    );
    signIn(cleverProvider.id, {
      callbackUrl,
    }).catch(() => {
      setSignInError(
        'We could not start Clever sign-in. Please try again in a few moments.'
      );
      setCleverLoading(false);
    });
  };

  const onSubmitLogin = async ({ email, password }: UserCreateFormData) => {
    const normalizedEmail = email.toLowerCase().trim();
    setSignInError('');
    setSignInErrorCode('');
    setLastAttemptedEmail(normalizedEmail);
    resetResendState();
    setLoggingIn(true);
    captureProductEvent(
      posthog,
      PRODUCT_ANALYTICS_EVENTS.AUTH_CREDENTIAL_SIGN_IN_SUBMITTED,
      {
        form_type: 'signin',
        has_callback_url: Boolean(router.query?.callbackUrl),
      }
    );

    const signInPromise = signIn<'credentials'>('credentials', {
      email: normalizedEmail,
      password: password,
      redirect: false,
    });

    const timeoutPromise = new Promise<SignInResponse | undefined>(
      (_, reject) =>
        setTimeout(() => {
          reject(new Error('SignInTimeout'));
        }, SIGNIN_TIMEOUT_MS)
    );

    try {
      const response = await Promise.race([signInPromise, timeoutPromise]);

      if (response?.ok && !response?.error) {
        captureProductEvent(
          posthog,
          PRODUCT_ANALYTICS_EVENTS.AUTH_CREDENTIAL_SIGN_IN_SUCCEEDED,
          {
            form_type: 'signin',
            destination_route_template: normalizeAnalyticsPath(callbackUrl),
          }
        );
        await router.push(callbackUrl);
      } else {
        const errorCode = response?.error ?? '';
        const errorMessage = errorCode
          ? mapSignInErrorToUserMessage(errorCode, {
              appName: brand.appName,
            })
          : 'Sign in failed. Please check your credentials.';
        setSignInErrorCode(errorCode);
        setSignInError(errorMessage);
        captureProductEvent(
          posthog,
          PRODUCT_ANALYTICS_EVENTS.AUTH_CREDENTIAL_SIGN_IN_FAILED,
          {
            form_type: 'signin',
            error_code: errorCode || 'unknown',
          }
        );
      }
    } catch (error: unknown) {
      if (error instanceof Error && error.message === 'SignInTimeout') {
        setSignInError(
          'Sign in timed out. Please check your network connection and try again.'
        );
      } else {
        setSignInError(
          'Sign in failed unexpectedly. Please try again. If the problem persists, try disabling browser extensions or using an Incognito window.'
        );
      }
      captureProductEvent(
        posthog,
        PRODUCT_ANALYTICS_EVENTS.AUTH_CREDENTIAL_SIGN_IN_FAILED,
        {
          form_type: 'signin',
          error_code:
            error instanceof Error && error.message === 'SignInTimeout'
              ? 'SignInTimeout'
              : 'unexpected',
        }
      );
    } finally {
      setLoggingIn(false);
    }
  };

  const onSubmitRegister = (registerData: UserCreateFormData) => {
    if (status === 'pending') return;
    signupTelemetryStateRef.current = null;
    captureProductEvent(
      posthog,
      PRODUCT_ANALYTICS_EVENTS.AUTH_SIGNUP_SUBMITTED,
      {
        form_type: 'register',
        has_product_key: Boolean(registerData.productKey?.trim()),
      }
    );
    createUser({
      email: registerData.email,
      password: registerData.password,
      firstName: registerData.firstName,
      lastName: registerData.lastName,
      productKey: registerData.productKey,
    });
  };

  const handleResendVerification = useCallback(
    async (targetEmail?: string) => {
      const resolvedEmail = (targetEmail || lastAttemptedEmail || '').trim();

      if (
        !resolvedEmail ||
        isResendingVerification ||
        (cooldownSecondsRemaining ?? 0) > 0
      ) {
        return;
      }

      setResendStatus({ kind: 'idle' });

      try {
        const response = await resendVerification(resolvedEmail);

        if (response.cooldownActive) {
          const retryAfterSeconds = Math.max(
            response.retryAfterSeconds ?? 60,
            1
          );
          setCooldownSecondsRemaining(retryAfterSeconds);
          setResendStatus({
            kind: 'cooldown',
            message: response.message,
            retryAfterSeconds,
          });
          return;
        }

        resetResendState();
        setResendStatus({
          kind: 'success',
          message: RESEND_SUCCESS_MESSAGE,
        });
      } catch {
        resetResendState();
        setResendStatus({
          kind: 'error',
          message: RESEND_FAILURE_MESSAGE,
        });
      }
    },
    [
      cooldownSecondsRemaining,
      isResendingVerification,
      lastAttemptedEmail,
      resendVerification,
      resetResendState,
    ]
  );

  useEffect(() => {
    setIsComponentLoading(false);
  }, []);

  useEffect(() => {
    if (!isRegisterForm) return;

    if (isSuccess && signupTelemetryStateRef.current !== 'success') {
      signupTelemetryStateRef.current = 'success';
      captureProductEvent(
        posthog,
        PRODUCT_ANALYTICS_EVENTS.AUTH_SIGNUP_SUCCEEDED,
        {
          form_type: 'register',
        }
      );
      return;
    }

    if (isError && signupTelemetryStateRef.current !== 'error') {
      signupTelemetryStateRef.current = 'error';
      captureProductEvent(
        posthog,
        PRODUCT_ANALYTICS_EVENTS.AUTH_SIGNUP_FAILED,
        {
          form_type: 'register',
          has_product_key: Boolean(methods.getValues('productKey')?.trim()),
        }
      );
    }
  }, [isError, isRegisterForm, isSuccess, methods, posthog]);

  useEffect(() => {
    if (!isRegisterForm && typeof routedErrorParam === 'string') {
      setSignInErrorCode(routedErrorParam);
      resetResendState();
      setSignInError(
        mapSignInErrorToUserMessage(routedErrorParam, {
          appName: brand.appName,
        })
      );
    }
  }, [brand.appName, isRegisterForm, resetResendState, routedErrorParam]);

  useEffect(() => {
    if (!lastAttemptedEmail) return;

    const normalizedCurrentEmail =
      typeof currentEmailValue === 'string'
        ? currentEmailValue.trim().toLowerCase()
        : '';

    if (normalizedCurrentEmail !== lastAttemptedEmail) {
      resetResendState();
      setLastAttemptedEmail('');
    }
  }, [currentEmailValue, lastAttemptedEmail, resetResendState]);

  useEffect(() => {
    if (resendStatus.kind !== 'cooldown') return;

    setCooldownSecondsRemaining(resendStatus.retryAfterSeconds);

    const timer = window.setInterval(() => {
      setCooldownSecondsRemaining((current) => {
        if (current == null || current <= 1) {
          window.clearInterval(timer);
          return null;
        }

        return current - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [resendStatus]);

  const showResendVerificationCta =
    !isRegisterForm &&
    signInErrorCode === EMAIL_NOT_VERIFIED_ERROR &&
    Boolean(lastAttemptedEmail);

  return (
    <div className="p-5 sm:max-w-md justify-center align-middle shadow rounded bg-white text-black">
      <div className="flex flex-col justify-between gap-4 mt-5">
        {isError && (
          <div className="flex">
            <Alert
              variant="error"
              message={getErrorMessage(error, {
                isRegisterForm,
                hasProductKey: Boolean(methods.getValues('productKey')?.trim()),
              })}
            />
          </div>
        )}
        {signInError && (
          <div className="flex flex-col gap-3">
            <Alert variant="error" message={signInError} />
            {showResendVerificationCta && (
              <ResendVerificationCallout
                email={lastAttemptedEmail}
                status={resendStatus}
                cooldownSecondsRemaining={cooldownSecondsRemaining}
                isLoading={isResendingVerification}
                onResend={() => {
                  void handleResendVerification();
                }}
              />
            )}
          </div>
        )}
        {isSuccess && (
          <AuthSuccessRegister
            email={data?.email}
            resendStatus={resendStatus}
            cooldownSecondsRemaining={cooldownSecondsRemaining}
            isResendingVerification={isResendingVerification}
            onBackToSignIn={() => void router.push(Routes.SITE.LOGIN)}
            onResend={(email) => {
              void handleResendVerification(email);
            }}
          />
        )}
        {!isSuccess && (
          <AuthCredentialForm
            methods={methods}
            isRegisterForm={isRegisterForm}
            onSubmitRegister={onSubmitRegister}
            onSubmitLogin={onSubmitLogin}
            loggingIn={loggingIn}
            isLoading={isLoading}
            loadingDashboard={loadingDashboard}
            showCleverSignIn={showCleverSignIn}
            handleCleverSignIn={handleCleverSignIn}
            cleverLoading={cleverLoading}
            isComponentLoading={isComponentLoading}
            isWebView={isWebView}
          />
        )}
      </div>
    </div>
  );
};
export default AuthForm;
